import max from 'lodash/max';
import min from 'lodash/min';
import { Point } from '../types/point';
import { Rect } from '../types/rect';

const angleThreshold = 1;

export interface ItemPositionProperties {
  position: { x: number; y: number };
  size: { width: number; height: number };
  angle: number;
}

export const calculateBoundingRect = ({
  position: { x, y },
  size: { width, height },
  angle,
}: ItemPositionProperties): Rect => {
  // don't recalculate for small angles (performance)
  if (angle < angleThreshold && angle > -angleThreshold) {
    return { x, y, width, height };
  }

  const topLeft = { x, y };
  const topRight = { x: x + width, y };
  const bottomLeft = { x, y: y + height };
  const bottomRight = {
    x: x + width,
    y: y + height,
  };
  const center = { x: x + width / 2, y: y + height / 2 };
  const rotatedPoints = [topLeft, topRight, bottomLeft, bottomRight].map((p) => rotateAroundPoint(p, center, angle));

  const minValue = {
    x: min(rotatedPoints.map((p) => p.x)) || 0,
    y: min(rotatedPoints.map((p) => p.y)) || 0,
  };
  const maxValue = {
    x: max(rotatedPoints.map((p) => p.x)) || 0,
    y: max(rotatedPoints.map((p) => p.y)) || 0,
  };

  return {
    x: minValue.x,
    y: minValue.y,
    width: maxValue.x - minValue.x,
    height: maxValue.y - minValue.y,
  };
};

export const rotateAroundPoint = (point: Point, center: Point, angle: number): Point => {
  const radians = angleToRadians(angle);
  const deltaX = point.x - center.x;
  const deltaY = point.y - center.y;

  const px = deltaX * Math.cos(radians) - deltaY * Math.sin(radians);
  const py = deltaX * Math.sin(radians) + deltaY * Math.cos(radians);

  const resX = px + center.x;
  const resY = py + center.y;

  return { x: resX, y: resY };
};

export const angleToRadians = (angle: number) => {
  angle = (angle < 0 ? 360 + angle : angle) % 360;
  return (Math.PI / 180) * angle;
};

export const rotateMousePosition = (center: Point, mousePos: Point, angle: number) => {
  const radians = angleToRadians(angle);

  const deltaX = mousePos.x - center.x;
  const deltaY = mousePos.y - center.y;

  const nx = center.x + Math.cos(radians) * deltaX + Math.sin(radians) * deltaY;
  const ny = center.y + Math.cos(radians) * deltaY - Math.sin(radians) * deltaX;

  return { x: nx, y: ny };
};

export const getMousePosition = (point: Point, element: SVGGraphicsElement) => {
  const CTM = element.getScreenCTM();
  return CTM
    ? {
        x: point.x / CTM.a,
        y: point.y / CTM.d,
      }
    : point;
};
